// Admin console: Persetujuan Pengguna (GET /admin/agreements, paged; export xlsx and csv).
//   header   plain spacing, hairline below   h1, count, Unduh Excel, Unduh CSV
//   table    rows                            table from 1024, stacked rows below
//   pager    plain spacing                   Sebelumnya, Halaman n dari m, Berikutnya
// The downloads are plain links to the export URL, as Database navigates to its own, so the session cookie rides along.
import { useState } from "react";
import * as API from "../api";
import { formatCount, formatDateTime } from "../lib/format";
import { ROLE_LABEL } from "../lib/labels";
import { EmptyState, Notice, PageHeader, SkeletonRows, useAsync, useTitle } from "../ui/components";
import { Glyph } from "../ui/icons";

export const AGREEMENTS_PAGE_SIZE = 20;

export function AgreementsScreen() {
  useTitle("Persetujuan Pengguna");
  const [page, setPage] = useState(1);
  const list = useAsync(() => API.listAgreements(page, AGREEMENTS_PAGE_SIZE), [page]);
  const rows = list.data?.agreements ?? [];
  const total = list.data?.total ?? 0;
  const pageCount = Math.max(Math.ceil(total / AGREEMENTS_PAGE_SIZE), 1);
  const roleLabel = (r: string) => ROLE_LABEL[r as API.Role] ?? r;

  return (
    <>
      <PageHeader
        divided
        title="Persetujuan Pengguna"
        lead={list.data ? `${formatCount(total)} persetujuan tercatat, terbaru di atas.` : "Memuat persetujuan"}
        actions={
          <div className="ag-export">
            <a className="btn" href={API.agreementsExportUrl("xlsx")} download>
              <Glyph name="documentDownload" size={20} />
              <span>Unduh Excel</span>
            </a>
            <a className="btn btn-secondary" href={API.agreementsExportUrl("csv")} download>
              <span>Unduh CSV</span>
            </a>
          </div>
        }
      />

      <section className="region" aria-label="Daftar persetujuan">
        {list.error ? (
          <Notice tone="danger" title="Gagal memuat persetujuan">
            <p>{API.errorMessage(list.error)}</p>
          </Notice>
        ) : null}

        {list.loading && !list.data ? (
          <SkeletonRows count={6} label="Memuat persetujuan" />
        ) : !list.error && rows.length === 0 ? (
          <EmptyState icon="shieldTick" title="Belum ada persetujuan" body="Persetujuan muncul di sini setelah pengguna baru menyetujui Persetujuan Pengguna." />
        ) : rows.length > 0 ? (
          <>
            <div className="records-table ag-table">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Nama</th>
                    <th scope="col">Email</th>
                    <th scope="col">Role</th>
                    <th scope="col">Versi</th>
                    <th scope="col">Disetujui</th>
                    <th scope="col">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((a) => (
                    <tr key={a.id}>
                      <td><span className="cell-text" title={a.userName}>{a.userName}</span></td>
                      <td><span className="cell-text" title={a.userEmail}>{a.userEmail}</span></td>
                      <td><span className="cell-text">{roleLabel(a.userRole)}</span></td>
                      <td><span className="cell-text">{a.version}</span></td>
                      <td><span className="cell-text">{formatDateTime(a.acceptedAt)}</span></td>
                      <td><span className="cell-text">{a.ip ?? "-"}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <ul className="rows records-list ag-list">
              {rows.map((a) => (
                <li key={a.id} className="ag-card">
                  <p className="ag-name">{a.userName}</p>
                  <p className="cell-meta ag-email">{a.userEmail}</p>
                  <dl className="ag-pairs">
                    <div className="ag-pair"><dt>Role</dt><dd>{roleLabel(a.userRole)}</dd></div>
                    <div className="ag-pair"><dt>Versi</dt><dd>{a.version}</dd></div>
                    <div className="ag-pair"><dt>Disetujui</dt><dd>{formatDateTime(a.acceptedAt)}</dd></div>
                    <div className="ag-pair"><dt>IP</dt><dd>{a.ip ?? "-"}</dd></div>
                  </dl>
                </li>
              ))}
            </ul>

            {pageCount > 1 ? (
              <div className="db-pager ag-pager" role="group" aria-label="Halaman persetujuan">
                <button type="button" className="btn btn-secondary" onClick={() => setPage((p) => Math.max(p - 1, 1))} disabled={page <= 1}>
                  Sebelumnya
                </button>
                <span className="cell-meta">
                  Halaman {page} dari {pageCount}
                </span>
                <button type="button" className="btn btn-secondary" onClick={() => setPage((p) => Math.min(p + 1, pageCount))} disabled={page >= pageCount}>
                  Berikutnya
                </button>
              </div>
            ) : null}
          </>
        ) : null}
      </section>
    </>
  );
}
